import { CSSProperties, useEffect, useRef, useState } from 'react';
import { EnquiresGridComponent } from '@vivli/features/enquiry/components';
import { useEnquiryListHook } from '@vivli/features/enquiry/infastructure/hook';
import { TabComponent, TabsComponent } from '@vivli/shared/components';
import { AssetsConstant } from '@vivli/shared/infrastructure/constants';
import { GridApi } from 'ag-grid-community';
import { useActiveUserContext } from '@vivli/core/infrastructure/context';
import { useNavigate } from 'react-router';
import { Size } from '@vivli/shared/theme';
import { DirStatusEnum, EnquiresTabEnum, EnquiryStatusEnum } from '@vivli/features/enquiry/infastructure/enum';
import { IDataInfoRequest, IEnquiry } from '@vivli/features/enquiry/infastructure/interface';
import { IUser } from '@vivli/shared/infrastructure/interface';
import { useOrganizations } from '@vivli/features/organizations/infrastructure/hook';
import { useUserPermissions } from '@vivli/features/users/infrastructure/hook';
import { ExportCsvButtonView } from '@vivli/shared/views';
import { PermissionsEnum } from '@vivli/shared/infrastructure/enum';

const containerStyle: CSSProperties = {
    height: '100%',
    padding: `0 ${Size.INSET_SIZE}px`,
    position: 'relative'
};
const searchInputStyle: CSSProperties = {
    flex: '1 1 auto',
    resize: 'none',
    padding: Size.PADDING,
    borderRadius: '8px',
    borderColor: 'transparent',
    boxShadow: 'rgba(0, 0, 0, 0.2) 0px 1px 20px, rgba(0, 0, 0, 0.2) 0px 1px 2px',
    color: 'black',
    position: 'absolute',
    top: '20px',
    right: '40px'
};

enum tabEnum {
    awaitingMyAction,
    draft,
    formCheck,
    accepted,
    withdrawn,
    archived
}

export const EnquiresFeature = () => {
    const [formCheckEnquires, setFormCheckEnquires] = useState<IEnquiry[]>();
    const [draftEnquires, setDraftEnquires] = useState<IEnquiry[]>();
    const [acceptedEnquires, setAcceptedEnquires] = useState<IEnquiry[]>();
    const [archivedEnquiries, setArchivedEnquiries] = useState<IEnquiry[]>();
    const [withdrawnEnquiries, setWithdrawnEnquiries] = useState<IEnquiry[]>();
    const [awaitingMyAction, setAwaitingMyAction] = useState<IEnquiry[]>();
    const [isDataContributor, setIsDataContributor] = useState(false);
    const [activeTab, setActiveTab] = useState<number>(tabEnum.draft);

    const [draftCount, setDraftCount] = useState(0);
    const [acceptedCount, setAcceptedCount] = useState(0);
    const [archivedCount, setArchivedCount] = useState(0);
    const [withdrawnCount, setWithdrawnCount] = useState(0);
    const [awaitingCount, setAwaitingCount] = useState(0);
    const [formCheckCount, setFormCheckCount] = useState(0);

    const formCheckGridApi = useRef<GridApi>(null);
    const draftGridApi = useRef<GridApi>(null);
    const acceptedGridApi = useRef<GridApi>(null);
    const archivedGridApi = useRef<GridApi>(null);
    const withdrawnGridApi = useRef<GridApi>(null);
    const awaitingMyActionGridApi = useRef<GridApi>(null);

    const { user, hasPermission, orgHasPermission } = useActiveUserContext();
    const { organizations } = useOrganizations();
    const navigate = useNavigate();
    const { enquires, isLoadingEnquires } = useEnquiryListHook();
    const { isOrgOrVivliAdmin } = useUserPermissions();
    const isVivliAdmin = hasPermission(PermissionsEnum.EnqActAdv);

    const addEnquiryTitle = !isOrgOrVivliAdmin ? `Add Enquiry` : null;

    const getDefaultTab = () => (isOrgOrVivliAdmin ? tabEnum.awaitingMyAction : tabEnum.draft);

    const setInitialTab = () => {
        const currentPath = window.location.pathname;
        const routePath = currentPath.replace('/admin/enquiries/', '')?.toLowerCase();

        if (!routePath) {
            return;
        }

        switch (routePath) {
            case 'formcheck':
                setActiveTab(tabEnum.formCheck);
                break;
            case 'accepted':
                setActiveTab(tabEnum.accepted);
                break;
            case 'withdrawn':
                setActiveTab(tabEnum.withdrawn);
                break;
            case 'archived':
                setActiveTab(tabEnum.archived);
                break;
            case 'awaitingmyaction':
                setActiveTab(tabEnum.awaitingMyAction);
                break;
            default:
                setActiveTab(getDefaultTab());
                break;
        }
    };

    const exportColumnKeysCommon = ['id', 'requesterName', 'enquiryPurpose', 'submittedDate', 'statusForDownload', 'dataInfoRequestsCount'];

    const exportColumnKeysReview = [
        'id',
        'requesterName',
        'enquiryPurpose',
        'updatedDate',
        'submittedDate',
        'statusForDownload',
        'dataInfoRequestsCount'
    ];

    const exportColumnKeysDraft = ['id', 'requesterName', 'enquiryPurpose', 'createdDate', 'statusForDownload', 'dataInfoRequestsCount'];

    const exportToCsv = (tab: tabEnum) => {
        const dateStr = new Date().toLocaleDateString().replace('/', '_');
        switch (tab) {
            case tabEnum.awaitingMyAction:
                awaitingMyActionGridApi.current.exportDataAsCsv({
                    fileName: `Awaiting_My_Action_${dateStr}`,
                    columnKeys: exportColumnKeysCommon
                });
                break;
            case tabEnum.formCheck:
                formCheckGridApi.current.exportDataAsCsv({
                    fileName: `Form_Check_Enquiries_${dateStr}`,
                    columnKeys: exportColumnKeysCommon
                });
                break;
            case tabEnum.accepted:
                acceptedGridApi.current.exportDataAsCsv({
                    fileName: `Accepted_Enquiries_${dateStr}`,
                    columnKeys: exportColumnKeysReview
                });
                break;
            case tabEnum.draft:
                draftGridApi.current.exportDataAsCsv({
                    fileName: `Draft_Enquiries_${dateStr}`,
                    columnKeys: exportColumnKeysDraft
                });
                break;
            case tabEnum.withdrawn:
                withdrawnGridApi.current.exportDataAsCsv({
                    fileName: `Withdrawn_Enquiries_${dateStr}`,
                    columnKeys: exportColumnKeysCommon
                });
                break;
            case tabEnum.archived:
                archivedGridApi.current.exportDataAsCsv({
                    fileName: `Archived_Enquiries_${dateStr}`,
                    columnKeys: exportColumnKeysCommon
                });
                break;
        }
    };

    const getExportButton = (tab: tabEnum) => {
        const exportBtnStyle: CSSProperties = {
            position: 'absolute',
            right: !!addEnquiryTitle ? 130 : 0
        };

        return <ExportCsvButtonView style={exportBtnStyle} onClick={() => exportToCsv(tab)} />;
    };

    const IsFinalStatus = (status) => {
        return status == DirStatusEnum.AvailableListed || status == DirStatusEnum.AvailableUnlisted || status == DirStatusEnum.NotAvailable;
    };

    const awaitingUserActionFilter = (user: IUser) => (eq) => {
        switch (eq.status) {
            case EnquiryStatusEnum.EnquiryValidation:
                //TODO PERMFE - redo with permissions+
                return isVivliAdmin;

            //TODO PERMFE - redo with permissions+
            case EnquiryStatusEnum.Review:
                return (
                    (isVivliAdmin && eq.dataInfoRequests.every((dir) => IsFinalStatus(dir.status))) ||
                    (isDataContributor &&
                        !isVivliAdmin &&
                        eq.dataInfoRequests.some(
                            (dir: IDataInfoRequest) =>
                                !IsFinalStatus(dir.status) && orgHasPermission(dir.orgId, PermissionsEnum.DataContrib)
                        ))
                );
            default:
                return false;
        }
    };

    const handleSearchChange = (searchValue: string) => {
        awaitingMyActionGridApi.current.setGridOption('quickFilterText', searchValue);
        draftGridApi.current.setGridOption('quickFilterText', searchValue);
        formCheckGridApi.current.setGridOption('quickFilterText', searchValue);
        acceptedGridApi.current.setGridOption('quickFilterText', searchValue);
        withdrawnGridApi.current.setGridOption('quickFilterText', searchValue);
        archivedGridApi.current.setGridOption('quickFilterText', searchValue);

        setDraftCount(draftGridApi.current.getDisplayedRowCount);
        setAcceptedCount(acceptedGridApi.current.getDisplayedRowCount);
        setWithdrawnCount(withdrawnGridApi.current.getDisplayedRowCount);
        setArchivedCount(archivedGridApi.current.getDisplayedRowCount);
        setAwaitingCount(awaitingMyActionGridApi.current.getDisplayedRowCount);
        setFormCheckCount(formCheckGridApi.current.getDisplayedRowCount);
    };

    useEffect(() => {
        setInitialTab();
    }, []);

    useEffect(() => {
        if (!enquires) {
            return;
        }
        const formCheckEnquires = enquires?.filter((s) => s.status === EnquiryStatusEnum.EnquiryValidation);
        const draftEnquires = enquires?.filter((s) => s.status === EnquiryStatusEnum.Draft);
        const acceptedEnquires = enquires?.filter((s) => s.status == EnquiryStatusEnum.Review);
        const withdrawnEnquiries = enquires?.filter((s) => s.status == EnquiryStatusEnum.Withdrawn);
        const archivedEnquiries = enquires?.filter((s) => s.status == EnquiryStatusEnum.Archived);
        const awaitingMyAction = enquires?.filter(awaitingUserActionFilter(user));

        setAcceptedEnquires(acceptedEnquires);
        setDraftEnquires(draftEnquires);
        setFormCheckEnquires(formCheckEnquires);
        setWithdrawnEnquiries(withdrawnEnquiries);
        setArchivedEnquiries(archivedEnquiries);
        setAwaitingMyAction(awaitingMyAction);

        setDraftCount(draftEnquires?.length || 0);
        setAcceptedCount(acceptedEnquires?.length || 0);
        setWithdrawnCount(withdrawnEnquiries?.length || 0);
        setArchivedCount(archivedEnquiries?.length || 0);
        setAwaitingCount(awaitingMyAction?.length || 0);
        setFormCheckCount(formCheckEnquires?.length || 0);
    }, [enquires]);

    useEffect(() => {
        if (!organizations) {
            return;
        }

        //TODO PERMFE - redo with permissions+
        const userIsDataContributor = organizations?.some((o) => orgHasPermission(o.id, PermissionsEnum.DataContrib));

        setIsDataContributor(userIsDataContributor);

        if (userIsDataContributor && activeTab === tabEnum.draft) {
            setActiveTab(tabEnum.awaitingMyAction);
        }
    }, [organizations]);

    return (
        <div style={containerStyle}>
            <input
                type="text"
                style={searchInputStyle}
                placeholder="Search enquiries"
                onChange={(e) => handleSearchChange(e.target.value)}
            />
            <TabsComponent
                defaultTab={activeTab}
                commandImage={AssetsConstant.PLUS_SYMBOL}
                commandTitle={addEnquiryTitle}
                title={'Enquiries'}
                subTitle={
                    'Questions to Vivli Members about the availability of their studies not listed on the Vivli platform, or additional information on studies that are listed'
                }
                commandOnClick={() => {
                    navigate('/admin/enquiry');
                }}
            >
                {
                    <TabComponent
                        hidden={!isVivliAdmin && !isDataContributor}
                        title={EnquiresTabEnum.AwaitingMyAction}
                        count={awaitingCount}
                        onTabClick={() => {
                            navigate('/admin/enquiries/accepted');
                        }}
                        rightHeaderContent={getExportButton(tabEnum.awaitingMyAction)}
                    >
                        <EnquiresGridComponent
                            type="Submitted"
                            enquires={awaitingMyAction}
                            onGridReady={(api) => (awaitingMyActionGridApi.current = api)}
                            dataRefreshing={isLoadingEnquires}
                            hideUpdatedColumn={true}
                            hideCreatedColumn={true}
                        />
                    </TabComponent>
                }

                <TabComponent
                    title={EnquiresTabEnum.Draft}
                    count={draftCount}
                    onTabClick={() => {
                        navigate('/admin/enquiries/draft');
                    }}
                    rightHeaderContent={getExportButton(tabEnum.draft)}
                >
                    <EnquiresGridComponent
                        type="Drafted"
                        enquires={draftEnquires}
                        onGridReady={(api) => (draftGridApi.current = api)}
                        dataRefreshing={isLoadingEnquires}
                        hideCreatedColumn={false}
                        hideUpdatedColumn={true}
                        hideDateColumn={true}
                    />
                </TabComponent>
                <TabComponent
                    title={EnquiresTabEnum.EnquiryValidation}
                    count={formCheckCount}
                    onTabClick={() => {
                        navigate('/admin/enquiries/formCheck');
                    }}
                    rightHeaderContent={getExportButton(tabEnum.formCheck)}
                >
                    <EnquiresGridComponent
                        type="Submitted"
                        enquires={formCheckEnquires}
                        onGridReady={(api) => (formCheckGridApi.current = api)}
                        dataRefreshing={isLoadingEnquires}
                        hideUpdatedColumn={true}
                        hideCreatedColumn={true}
                    />
                </TabComponent>
                <TabComponent
                    title={EnquiresTabEnum.Review}
                    count={acceptedCount}
                    onTabClick={() => {
                        navigate('/admin/enquiries/accepted');
                    }}
                    rightHeaderContent={getExportButton(tabEnum.accepted)}
                >
                    <EnquiresGridComponent
                        type="Submitted" //we do not have a date stored that reliably has Submitted for Review value
                        enquires={acceptedEnquires}
                        onGridReady={(api) => (acceptedGridApi.current = api)}
                        dataRefreshing={isLoadingEnquires}
                        hideCreatedColumn={true}
                        hideUpdatedColumn={false}
                        hideDateColumn={false}
                    />
                </TabComponent>
                <TabComponent
                    title={EnquiresTabEnum.Withdrawn}
                    count={withdrawnCount}
                    onTabClick={() => {
                        navigate('/admin/enquiries/accepted');
                    }}
                    rightHeaderContent={getExportButton(tabEnum.withdrawn)}
                >
                    <EnquiresGridComponent
                        type="Submitted"
                        enquires={withdrawnEnquiries}
                        onGridReady={(api) => (withdrawnGridApi.current = api)}
                        dataRefreshing={isLoadingEnquires}
                        hideUpdatedColumn={true}
                        hideCreatedColumn={true}
                    />
                </TabComponent>
                <TabComponent
                    title={EnquiresTabEnum.Archived}
                    count={archivedCount}
                    onTabClick={() => {
                        navigate('/admin/enquiries/accepted');
                    }}
                    rightHeaderContent={getExportButton(tabEnum.archived)}
                >
                    <EnquiresGridComponent
                        type="Submitted"
                        enquires={archivedEnquiries}
                        onGridReady={(api) => (archivedGridApi.current = api)}
                        dataRefreshing={isLoadingEnquires}
                        hideUpdatedColumn={true}
                        hideCreatedColumn={true}
                    />
                </TabComponent>
            </TabsComponent>
        </div>
    );
};
